// Page builder block model (MOD-14 / Blok F).
//
// This is the FE mirror of the BE `BlockSchemaRegistry`. The BE is the
// source-of-truth for validation (it answers PB_INVALID_BLOCK on save), so
// these zod schemas only power instant client-side hints and typing — never
// a replacement for the server check. URL fields reuse `isSafeUrl` so the FE
// rejects `javascript:`/`//host` before the round-trip, matching BE safe-url.

import { z } from 'zod';
import { isSafeUrl } from '@shared/utils/safeUrl';

export const BLOCK_TYPES = [
  'hero',
  'richText',
  'cardGrid',
  'cta',
  'formEmbed',
  'sliderEmbed',
  'mapEmbed',
] as const;

export type BlockType = (typeof BLOCK_TYPES)[number];

// MOD-17 / K-18 — runtime Component Studio blokları. Bir published component'in
// blok anahtarı `cmp_{Id:N}` (32 hex, tiresiz GUID) formundadır — BE
// `RuntimeComponentBlock.Prefix` ile birebir. `cmp_` prefix'i statik BlockType
// union'ının DIŞINDA tutulur (çakışmaz) ama SAKLANABİLİR bir blok tipidir.
export const RUNTIME_BLOCK_PREFIX = 'cmp_';
const RUNTIME_BLOCK_REGEX = /^cmp_[0-9a-fA-F]{32}$/;

export type RuntimeBlockKey = `cmp_${string}`;

/** Statik yerleşik blok + runtime component bloğu — saklanabilir tüm blok tipleri. */
export type PersistableBlockType = BlockType | RuntimeBlockKey;

export function isRuntimeBlockKey(value: string): value is RuntimeBlockKey {
  return RUNTIME_BLOCK_REGEX.test(value);
}

/** Max cards a `cardGrid` may hold (mirrors BE). */
export const CARD_GRID_MAX = 12;

const safeUrlField = z
  .string()
  .refine((v) => isSafeUrl(v), { message: 'validation.safeUrl' });

const optionalSafeUrl = safeUrlField.optional().or(z.literal(''));

export const heroSchema = z.object({
  title: z.string().min(1),
  subtitle: z.string().optional(),
  imageUrl: optionalSafeUrl,
  ctaText: z.string().optional(),
  ctaLink: optionalSafeUrl,
});

export const richTextSchema = z.object({
  html: z.string(),
});

export const cardSchema = z.object({
  title: z.string().min(1),
  text: z.string().optional(),
  imageUrl: optionalSafeUrl,
  link: optionalSafeUrl,
});

export const cardGridSchema = z.object({
  cards: z.array(cardSchema).max(CARD_GRID_MAX),
});

export const ctaSchema = z.object({
  title: z.string().min(1),
  text: z.string().optional(),
  buttonText: z.string().min(1),
  buttonLink: safeUrlField,
});

export const formEmbedSchema = z.object({
  formId: z.string().min(1),
});

export const sliderEmbedSchema = z.object({
  sliderId: z.string().min(1),
});

export const mapEmbedSchema = z.object({
  lat: z.number(),
  lng: z.number(),
  zoom: z.number().optional(),
  title: z.string().optional(),
});

export const BLOCK_SCHEMAS = {
  hero: heroSchema,
  richText: richTextSchema,
  cardGrid: cardGridSchema,
  cta: ctaSchema,
  formEmbed: formEmbedSchema,
  sliderEmbed: sliderEmbedSchema,
  mapEmbed: mapEmbedSchema,
} as const satisfies Record<BlockType, z.ZodTypeAny>;

export type HeroProps = z.infer<typeof heroSchema>;
export type RichTextProps = z.infer<typeof richTextSchema>;
export type CardGridProps = z.infer<typeof cardGridSchema>;
export type CardProps = z.infer<typeof cardSchema>;
export type CtaProps = z.infer<typeof ctaSchema>;
export type FormEmbedProps = z.infer<typeof formEmbedSchema>;
export type SliderEmbedProps = z.infer<typeof sliderEmbedSchema>;
export type MapEmbedProps = z.infer<typeof mapEmbedSchema>;

/** A single persisted block: `{ blockType, props }` — matches BE PUT payload. */
export interface PageBlock {
  blockType: PersistableBlockType;
  props: Record<string, unknown>;
}

/**
 * Saklanabilir bir blok tipi mi? Statik yerleşikleri VE runtime `cmp_` component
 * anahtarlarını kabul eder. KRİTİK (R-3): `mapping.ts` (kaydet) ve `pageBlocks.ts`
 * (yükle) filtreleri bunu kullanır — `cmp_` prefix'i burada kapsanmazsa runtime
 * bloklar round-trip'te SESSİZCE DÜŞER (palete gelir ama kaydedilmez/geri okunmaz).
 */
export function isBlockType(value: string): value is PersistableBlockType {
  return (BLOCK_TYPES as readonly string[]).includes(value) || isRuntimeBlockKey(value);
}

/** Yalnız statik yerleşik blok tipi mi (runtime component HARİÇ)? */
export function isStaticBlockType(value: string): value is BlockType {
  return (BLOCK_TYPES as readonly string[]).includes(value);
}

/** Default props for a freshly inserted block (used by the Puck palette). */
export function defaultBlockProps(type: BlockType): Record<string, unknown> {
  switch (type) {
    case 'hero':
      return { title: '', subtitle: '', imageUrl: '', ctaText: '', ctaLink: '' };
    case 'richText':
      return { html: '' };
    case 'cardGrid':
      return { cards: [] };
    case 'cta':
      return { title: '', text: '', buttonText: '', buttonLink: '' };
    case 'formEmbed':
      return { formId: '' };
    case 'sliderEmbed':
      return { sliderId: '' };
    case 'mapEmbed':
      return { lat: 41.0082, lng: 28.9784, zoom: 12, title: '' };
    default:
      return {};
  }
}
